import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, Heart, User, Search, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Boutique', href: '/shop' },
    { name: 'Nouveautés', href: '/shop?is_new=true' },
    { name: 'Collections', href: '/shop?is_featured=true' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="hover:opacity-90 transition-opacity" data-testid="logo-link">
            <img
              src="/images/logo-maison-ka.png"
              alt="MAISON KA COUTURE"
              className="h-9 md:h-11 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-12">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="gold-underline text-sm uppercase tracking-widest text-foreground/70 hover:text-gold transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Icons */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 hover:text-gold transition-colors"
              data-testid="search-btn"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>
            
            <Link 
              to="/wishlist" 
              className="p-2 hover:text-gold transition-colors relative"
              data-testid="wishlist-link"
            >
              <Heart size={20} strokeWidth={1.5} />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-gold text-black text-[10px] flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>
            
            <Link 
              to="/cart" 
              className="p-2 hover:text-gold transition-colors relative"
              data-testid="cart-link"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-gold text-black text-[10px] flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            
            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="p-2 hover:text-gold transition-colors"
                    data-testid="admin-link"
                    title="Espace Créateur"
                  >
                    <LayoutDashboard size={20} strokeWidth={1.5} />
                  </Link>
                )}
                <Link 
                  to="/profile" 
                  className="p-2 hover:text-gold transition-colors"
                  data-testid="profile-link"
                >
                  <User size={20} strokeWidth={1.5} />
                </Link>
                <button 
                  onClick={handleLogout}
                  className="p-2 hover:text-gold transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut size={20} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <Link 
                to="/auth" 
                className="hidden md:block p-2 hover:text-gold transition-colors"
                data-testid="auth-link"
              >
                <User size={20} strokeWidth={1.5} />
              </Link>
            )}
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 hover:text-gold transition-colors"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.form
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
              onSubmit={handleSearch}
            >
              <div className="py-4 border-t border-black/10">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full bg-transparent border-b border-black/20 py-2 focus:border-gold focus:outline-none text-foreground placeholder:text-foreground/40"
                  data-testid="search-input"
                  autoFocus
                />
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-t border-black/10"
          >
            <div className="px-6 py-8 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block text-lg uppercase tracking-widest text-foreground/70 hover:text-gold transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-black/10">
                {isAuthenticated ? (
                  <>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block text-lg uppercase tracking-widest text-gold hover:text-gold/80 transition-colors mb-4"
                        onClick={() => setIsMenuOpen(false)}
                        data-testid="mobile-admin-link"
                      >
                        Espace Créateur
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="block text-lg uppercase tracking-widest text-foreground/70 hover:text-gold transition-colors mb-4"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Mon Compte
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                      className="block text-lg uppercase tracking-widest text-foreground/70 hover:text-gold transition-colors"
                    >
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <Link
                    to="/auth"
                    className="block text-lg uppercase tracking-widest text-foreground/70 hover:text-gold transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Connexion
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
